import { useEffect, useMemo, useState } from 'react';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    closestCenter,
    pointerWithin,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
    type CollisionDetection,
    type DragEndEvent,
    type DragStartEvent,
} from '@dnd-kit/core';
import { CustomModal } from '../CustomModal';
import {
    Btn,
    Calendar,
    IconClose,
    IconImage,
    IconPdf,
    IconSearch,
    IconVideo2,
    Scheduler,
    formatDateLabel,
    toDateKey,
    type CalendarDayMeta,
} from '../ui';
import {
    buildCompanyScheduleDraft,
    isPdfBriefingTopic,
    MAX_SCHEDULE_DRAFT_DATE_CHANGES,
    type BriefingTopic,
    type ScheduleDraftEntry,
} from './dailyBriefingData';
import { StyleBadge } from './TodaysBriefingCard';

export type { ScheduleDraftEntry };

function TopicTypeIcon({ topic }: { topic: BriefingTopic }) {
    if (topic.type === 'video') {
        return <IconVideo2 iconClasses="scheduler-type-svg" />;
    }
    if (isPdfBriefingTopic(topic)) {
        return <IconPdf iconClasses="scheduler-type-svg" />;
    }
    return <IconImage iconClasses="scheduler-type-svg" />;
}

function topicTypeClass(topic: BriefingTopic): string {
    if (topic.type === 'video') return 'scheduler-type-icon--video';
    if (isPdfBriefingTopic(topic)) return 'scheduler-type-icon--pdf';
    return 'scheduler-type-icon--poster';
}

function topicDragId(topicId: string): string {
    return `topic:${topicId}`;
}

function dayDropId(dateKey: string): string {
    return `day:${dateKey}`;
}

function parseTopicDragId(id: string): string | null {
    return id.startsWith('topic:') ? id.slice('topic:'.length) : null;
}

function parseDayDropId(id: string): string | null {
    return id.startsWith('day:') ? id.slice('day:'.length) : null;
}

const scheduleCollisionDetection: CollisionDetection = (args) => {
    const pointerHits = pointerWithin(args);
    if (pointerHits.length > 0) return pointerHits;
    return closestCenter(args);
};

function countChangedScheduleDates(
    baseline: Record<string, ScheduleDraftEntry>,
    draft: Record<string, ScheduleDraftEntry>,
    todayKey: string,
): number {
    const keys = new Set([...Object.keys(baseline), ...Object.keys(draft)]);
    let count = 0;
    for (const dateKey of keys) {
        if (dateKey < todayKey) continue;
        const baseEntry = baseline[dateKey];
        const draftEntry = draft[dateKey];
        if (!baseEntry && !draftEntry) continue;
        if (!baseEntry || !draftEntry || baseEntry.topicId !== draftEntry.topicId) {
            count += 1;
        }
    }
    return count;
}

function scheduleDraftIsDirty(
    baseline: Record<string, ScheduleDraftEntry>,
    draft: Record<string, ScheduleDraftEntry>,
    todayKey: string,
): boolean {
    return countChangedScheduleDates(baseline, draft, todayKey) > 0;
}

interface DraggableTopicRowProps {
    topic: BriefingTopic;
    canAssignWithKeyboard: boolean;
    onAssignWithKeyboard: (topic: BriefingTopic) => void;
}

function DraggableTopicRow({
    topic,
    canAssignWithKeyboard,
    onAssignWithKeyboard,
}: DraggableTopicRowProps) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: topicDragId(topic.id),
        data: { topic },
    });

    return (
        <li
            ref={setNodeRef}
            className={`scheduler-item${isDragging ? ' is-dragging' : ''}${canAssignWithKeyboard ? ' is-keyboard-assignable' : ''}`}
            {...listeners}
            {...attributes}
            aria-label={
                canAssignWithKeyboard
                    ? `${topic.title}. Press Enter to assign to selected days.`
                    : topic.title
            }
            onKeyDown={(event) => {
                if (!canAssignWithKeyboard) return;
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    onAssignWithKeyboard(topic);
                }
            }}
        >
            <span className={`scheduler-type-icon ${topicTypeClass(topic)}`} aria-hidden="true">
                <TopicTypeIcon topic={topic} />
            </span>
            <span className="scheduler-item-copy">
                <span className="scheduler-item-title">{topic.title}</span>
                <span className="scheduler-item-meta">
                    <StyleBadge style={topic.style} />
                    <span>{topic.displayId}</span>
                </span>
            </span>
        </li>
    );
}

interface ScheduleDayCellProps {
    day: CalendarDayMeta;
    entry: ScheduleDraftEntry | undefined;
    selected: boolean;
    isDropTarget: boolean;
    isPast: boolean;
    onToggleSelect: (dateKey: string) => void;
    onRequestRemove: (dateKey: string) => void;
}

function ScheduleDayCell({
    day,
    entry,
    selected,
    isDropTarget,
    isPast,
    onToggleSelect,
    onRequestRemove,
}: ScheduleDayCellProps) {
    const occupied = Boolean(entry);
    const editable = !isPast;
    const { setNodeRef, isOver } = useDroppable({
        id: dayDropId(day.dateKey),
        disabled: occupied || isPast,
        data: { dateKey: day.dateKey, occupied, isPast },
    });

    const className = [
        'calendar-day',
        occupied ? 'is-occupied' : 'is-empty',
        selected ? 'is-selected' : '',
        day.isToday ? 'is-today' : '',
        isPast ? 'is-past' : '',
        isOver && editable && !occupied ? 'is-over' : '',
        isDropTarget && editable && !occupied ? 'is-drop-hint' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            ref={setNodeRef}
            className={className}
            role={occupied || isPast ? 'group' : 'button'}
            tabIndex={occupied || isPast ? undefined : 0}
            aria-pressed={occupied || isPast ? undefined : selected}
            aria-label={
                occupied
                    ? `${formatDateLabel(day.dateKey)}${day.isToday ? ' (today)' : ''}: ${entry?.title}${isPast ? ' (past, read-only)' : ''}`
                    : isPast
                      ? `${formatDateLabel(day.dateKey)}${day.isToday ? ' (today)' : ''} (past, read-only)`
                      : `${formatDateLabel(day.dateKey)}${day.isToday ? ' (today)' : ''}${selected ? ', selected' : ''}`
            }
            onClick={() => {
                if (!editable || occupied) return;
                onToggleSelect(day.dateKey);
            }}
            onKeyDown={(event) => {
                if (!editable || occupied) return;
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onToggleSelect(day.dateKey);
                }
            }}
        >
            <span className="calendar-day-num">{day.dayNumber}</span>
            {entry ? (
                <div className="calendar-day-chip">
                    <span className="calendar-day-chip-title" title={entry.title}>
                        {entry.title}
                    </span>
                    {editable ? (
                        <Btn
                            variant="icon"
                            ariaLabel={`Remove ${entry.title} from ${formatDateLabel(day.dateKey)}`}
                            buttonIcon={<IconClose />}
                            buttonClasses="calendar-day-remove"
                            buttonOnClick={(event) => {
                                event.stopPropagation();
                                onRequestRemove(day.dateKey);
                            }}
                        />
                    ) : null}
                </div>
            ) : (
                <span className="calendar-day-placeholder">
                    {isPast ? '' : selected ? 'Press Enter on a topic to assign' : 'Drop topic or select'}
                </span>
            )}
        </div>
    );
}

interface ScheduleTopicsModalProps {
    visible: boolean;
    companyId: string;
    topics: BriefingTopic[];
    saving?: boolean;
    onDismiss: () => void;
    onSave: (payload: {
        draft: Record<string, ScheduleDraftEntry>;
        baseline: Record<string, ScheduleDraftEntry>;
    }) => Promise<void>;
}

export function ScheduleTopicsModal({
    visible,
    companyId,
    topics,
    saving = false,
    onDismiss,
    onSave,
}: ScheduleTopicsModalProps) {
    const todayKey = useMemo(() => toDateKey(new Date()), []);
    const now = useMemo(() => new Date(), []);
    const [viewYear, setViewYear] = useState(now.getFullYear());
    const [viewMonth, setViewMonth] = useState(now.getMonth());
    const [searchQuery, setSearchQuery] = useState('');
    const [baseline, setBaseline] = useState<Record<string, ScheduleDraftEntry>>({});
    const [draft, setDraft] = useState<Record<string, ScheduleDraftEntry>>({});
    const [selectedDays, setSelectedDays] = useState<string[]>([]);
    const [activeTopicId, setActiveTopicId] = useState<string | null>(null);
    const [pendingRemoveKey, setPendingRemoveKey] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        if (!visible) {
            setBaseline({});
            setDraft({});
            setSelectedDays([]);
            setSearchQuery('');
            setActiveTopicId(null);
            setPendingRemoveKey(null);
            setSaveError(null);
            setViewYear(new Date().getFullYear());
            setViewMonth(new Date().getMonth());
            return;
        }

        const seeded = buildCompanyScheduleDraft(topics, companyId);
        setBaseline(seeded);
        setDraft(seeded);
        setSaveError(null);
    }, [visible, companyId, topics]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
    );

    const filteredTopics = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return topics;
        return topics.filter(
            (topic) =>
                topic.title.toLowerCase().includes(q) ||
                topic.displayId.toLowerCase().includes(q),
        );
    }, [searchQuery, topics]);

    const isDirty = useMemo(
        () => scheduleDraftIsDirty(baseline, draft, todayKey),
        [baseline, draft, todayKey],
    );

    const changedDateCount = useMemo(
        () => countChangedScheduleDates(baseline, draft, todayKey),
        [baseline, draft, todayKey],
    );
    const exceedsChangeLimit = changedDateCount > MAX_SCHEDULE_DRAFT_DATE_CHANGES;

    const activeTopic = activeTopicId
        ? topics.find((topic) => topic.id === activeTopicId) ?? null
        : null;

    const pendingRemoveEntry = pendingRemoveKey ? draft[pendingRemoveKey] : undefined;

    const toggleSelect = (dateKey: string) => {
        if (dateKey < todayKey) return;
        setSelectedDays((prev) =>
            prev.includes(dateKey) ? prev.filter((key) => key !== dateKey) : [...prev, dateKey],
        );
    };

    const assignTopicToDateKeys = (topic: BriefingTopic, dateKeys: string[]) => {
        const editableKeys = dateKeys.filter((dateKey) => dateKey >= todayKey);
        if (editableKeys.length === 0) return;
        setDraft((prev) => {
            const next = { ...prev };
            for (const dateKey of editableKeys) {
                if (next[dateKey]) continue;
                next[dateKey] = {
                    dateKey,
                    topicId: topic.id,
                    topicType: topic.type,
                    title: topic.title,
                    style: topic.style,
                };
            }
            return next;
        });
        setSelectedDays([]);
    };

    const assignTopicWithKeyboard = (topic: BriefingTopic) => {
        const targetKeys = selectedDays.filter((key) => !draft[key] && key >= todayKey);
        if (targetKeys.length === 0) return;
        assignTopicToDateKeys(topic, targetKeys);
    };

    const handleDragStart = (event: DragStartEvent) => {
        const topicId = parseTopicDragId(String(event.active.id));
        setActiveTopicId(topicId);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveTopicId(null);
        const topicId = parseTopicDragId(String(event.active.id));
        if (!topicId || !event.over) return;

        const dropDateKey = parseDayDropId(String(event.over.id));
        if (!dropDateKey || dropDateKey < todayKey) return;
        if (draft[dropDateKey]) return;

        const topic = topics.find((item) => item.id === topicId);
        if (!topic) return;

        const selectionIncludesDrop = selectedDays.includes(dropDateKey);
        const targetKeys =
            selectionIncludesDrop && selectedDays.length > 0
                ? selectedDays.filter((key) => !draft[key] && key >= todayKey)
                : [dropDateKey];

        assignTopicToDateKeys(topic, targetKeys);
    };

    const handleDragCancel = () => {
        setActiveTopicId(null);
    };

    const confirmRemove = () => {
        if (!pendingRemoveKey || pendingRemoveKey < todayKey) return;
        setDraft((prev) => {
            const next = { ...prev };
            delete next[pendingRemoveKey];
            return next;
        });
        setPendingRemoveKey(null);
    };

    const handleSave = async () => {
        if (!isDirty || saving || exceedsChangeLimit) return;
        setSaveError(null);
        try {
            await onSave({ draft, baseline });
        } catch (err) {
            const detail = err instanceof Error && err.message ? err.message : 'Please try again.';
            setSaveError(detail);
        }
    };

    return (
        <>
            <CustomModal
                visible={visible}
                onDismiss={() => {
                    if (saving) return;
                    onDismiss();
                }}
                header="Schedule Topics"
                size="xlarge"
                contentClassName="db-schedule-modal-panel"
                bodyClassName="db-schedule-modal-body"
                describedById="scheduler-help"
                footer={
                    <div className="db-modal-footer-actions">
                        {exceedsChangeLimit ? (
                            <p className="db-modal-error" role="alert">
                                Too many date changes ({changedDateCount}). Limit is{' '}
                                {MAX_SCHEDULE_DRAFT_DATE_CHANGES} days per save — save in smaller batches.
                            </p>
                        ) : null}
                        <Btn
                            variant="secondary"
                            buttonText="Cancel"
                            buttonOnClick={onDismiss}
                            buttonDisabled={saving}
                        />
                        <Btn
                            variant="primary"
                            buttonText={saving ? 'Saving…' : 'Save schedule'}
                            buttonOnClick={handleSave}
                            buttonDisabled={saving || !isDirty || exceedsChangeLimit}
                        />
                    </div>
                }
            >
                <p id="scheduler-help" className="scheduler-help">
                    Drag a topic onto an empty day (today or future), or select empty days with the
                    keyboard then press Enter on a topic to assign. Occupied days can’t be
                    overwritten — remove first. Past days are read-only.
                </p>
                {saveError ? (
                    <p className="db-modal-error" role="alert">
                        {saveError}
                    </p>
                ) : null}

                <DndContext
                    sensors={sensors}
                    collisionDetection={scheduleCollisionDetection}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                >
                    <Scheduler
                        sidebarLabel="Topic library"
                        sidebar={
                            <>
                                <label className="scheduler-search">
                                    <IconSearch iconClasses="scheduler-search-icon" />
                                    <span className="visually-hidden">Search topic library</span>
                                    <input
                                        type="search"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="scheduler-search-input"
                                        placeholder="Search by name or ID…"
                                        disabled={saving}
                                    />
                                </label>
                                {filteredTopics.length === 0 ? (
                                    <p className="scheduler-empty-list">
                                        No topics match your search.
                                    </p>
                                ) : (
                                    <ul className="scheduler-item-list">
                                        {filteredTopics.map((topic) => (
                                            <DraggableTopicRow
                                                key={topic.id}
                                                topic={topic}
                                                canAssignWithKeyboard={
                                                    selectedDays.filter(
                                                        (key) => !draft[key] && key >= todayKey,
                                                    ).length > 0
                                                }
                                                onAssignWithKeyboard={assignTopicWithKeyboard}
                                            />
                                        ))}
                                    </ul>
                                )}
                            </>
                        }
                    >
                        <Calendar
                            year={viewYear}
                            month={viewMonth}
                            todayKey={todayKey}
                            ariaLabel="Schedule calendar"
                            onMonthChange={(nextYear, nextMonth) => {
                                setViewYear(nextYear);
                                setViewMonth(nextMonth);
                            }}
                            renderDay={(day) => (
                                <ScheduleDayCell
                                    day={day}
                                    entry={draft[day.dateKey]}
                                    selected={selectedDays.includes(day.dateKey)}
                                    isDropTarget={Boolean(activeTopicId)}
                                    isPast={day.dateKey < todayKey}
                                    onToggleSelect={toggleSelect}
                                    onRequestRemove={setPendingRemoveKey}
                                />
                            )}
                        />
                    </Scheduler>

                    <DragOverlay>
                        {activeTopic ? (
                            <div className="scheduler-drag-overlay">
                                <span
                                    className={`scheduler-type-icon ${topicTypeClass(activeTopic)}`}
                                    aria-hidden="true"
                                >
                                    <TopicTypeIcon topic={activeTopic} />
                                </span>
                                <span>{activeTopic.title}</span>
                            </div>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            </CustomModal>

            <CustomModal
                visible={Boolean(pendingRemoveKey && pendingRemoveEntry)}
                onDismiss={() => setPendingRemoveKey(null)}
                header="Remove scheduled topic"
                overlayClassName="db-schedule-confirm-overlay"
                footer={
                    <div className="db-modal-footer-actions">
                        <Btn
                            variant="secondary"
                            buttonText="Cancel"
                            buttonOnClick={() => setPendingRemoveKey(null)}
                            buttonDisabled={saving}
                        />
                        <Btn
                            variant="danger"
                            buttonText="Remove"
                            buttonOnClick={confirmRemove}
                            buttonDisabled={saving}
                        />
                    </div>
                }
            >
                <p>
                    Remove “{pendingRemoveEntry?.title}” from{' '}
                    {pendingRemoveKey ? formatDateLabel(pendingRemoveKey) : ''}? Save the schedule
                    to apply this change.
                </p>
            </CustomModal>
        </>
    );
}
